import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Tooltip } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { IoMdInformationCircleOutline } from 'react-icons/io'
import { useHistory } from 'react-router-dom'
import Select from 'react-select'
import { ImSpinner8 } from 'react-icons/im'

import GlobalInputComponent from '../../../../../../components/events/forms/detail/Components/GlobalInputComponent'
import { IconSearch } from '../../../../../../components/icons/IconSearch'
import { PrivateLiveToggle } from '../../../../../../components/elements/toggles/toggles/PrivateLiveToggle'
import GapTimeSelector from '../../../../../../components/events/forms/scheduling/components/GapTimeSelector'

import { formatOptionLabel } from '../../../../../../helpers/select'
import useToasterHelper from '../../../../../../helpers/ToasterHelper'
import { getRegistrationFee, sortByOrder, updateScoreStatus } from '../../data/operations-tab-utils'
import { formatNotificationTimestamp } from '../../../../../../helpers/time'
import { getConvertedData } from '../../../../../../models/interface.helper'

import { IBreakRegistrationByDay } from '../../../../../../models/events/event.interface'
import { EventSchedulingModel } from '../../../../../../models/operations-tab/operations-tab.model'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { RegistrationByDayModel } from '../../../../../../models/registrations-by-day/registrationByDay.model'
import { EventReviewPublishModel } from '../../../../../../models/event-review-publish/event-review-publish.model'
import { TGroupPublish } from '../../../../../../models/event-scheduling/event-scheduling.interface'

import { HandleModalContext } from '../../../../../../layout/mainlayout/MainLayout'
import {
  IEventFees,
  RegistrationFeesType,
} from '../../../../../../models/event-fees/event-fees.interface'
import FirestoreService from '../../../../../../services/firestoreService'
import { DateEnum, MANAGE_OPERATIONS_CONSTS } from '../../../data/operations.data.const'
import { IOperationsTabs, IUpdateFeesKeys } from '../../ManageClinicNOtherOperationsTab'
import { IManageOption } from '../../types/IManageOption'
import { PublishItem } from '../../types/publish.types'
import { IEventReviewPublish } from '../../../../../../models/event-review-publish/event-review-publish.interface'

import { CONST } from '../../../../../../const/const'
import { MESSAGES_CONST } from '../../../../../../const/messages-const'
import { MODAL_CONSTS } from '../../../../../../const/modal-const'
import { customSelectStyles } from '../../../../../../customStyles/ReactSelectCustomStyle'
import { PAYMENT_STATUS, PAYMENT_TYPE } from '../../../../../../types/payment'
import {
  HOURS_OPTIONS,
  MINUTES_OPTIONS,
  SECONDS_OPTIONS,
} from '../../../../../../fakeData/TimeList'
import { getCurrency } from '../../../../../../helpers/payment'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

const eventSchedulingFormValidationSchema = EventSchedulingModel.validationSchema()

interface OperationsTabsProps {
  activeTab: IOperationsTabs
  setActiveTab: (value: IOperationsTabs) => void
  selectedClass: IManageOption | null
  setSelectedClass: (data: IManageOption) => void
  classOptions: IManageOption[]
  dayOptions: IManageOption[]
  selectedDay: IManageOption | null
  setSelectedDay: (data: IManageOption) => void
  registrationFees: IEventFees | null
  setIncrement: (value: string) => void
  updateFeesKeys: IUpdateFeesKeys
  fee: RegistrationFeesType | null
  eventId: string
  scheduleBy: 'class' | 'rider'
  registrationsByDay: IRegistrationByDayInterface[] | null
  setRegistrationsByDay: (value: IRegistrationByDayInterface[]) => void
  registrationsGroupedByDay: Record<string, any[]>
  setRegistrationsGroupedByDay: (value: { [key: string]: IBreakRegistrationByDay[] }) => void
  searchValue: string
  setSearchValue: (term: string) => void
  setGapTime: (time: string) => void
  clashCount?: number
  isTableInEditMode?: boolean
  publishedEvent: IEventReviewPublish | null
  setPublishedEvent: (publishedEvent: IEventReviewPublish | null) => void
}

export const OperationsHeader: FC<OperationsTabsProps> = ({
  activeTab,
  setActiveTab,
  selectedClass,
  setSelectedClass,
  classOptions,
  selectedDay,
  setSelectedDay,
  dayOptions,
  registrationFees,
  updateFeesKeys,
  setIncrement,
  registrationsGroupedByDay,
  setRegistrationsByDay,
  registrationsByDay,
  eventId,
  fee,
  scheduleBy,
  setRegistrationsGroupedByDay,
  searchValue,
  setSearchValue,
  setGapTime,
  clashCount,
  isTableInEditMode,
  publishedEvent,
  setPublishedEvent,
}) => {
  const history = useHistory()
  const toastFunctions = useToasterHelper()
  const handleModalContext = useContext(HandleModalContext)

  const isClassDropdown = activeTab === 'prize-money' || scheduleBy === 'class'

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  const { control } = useForm({
    defaultValues: { title: '' },
    resolver: yupResolver(eventSchedulingFormValidationSchema),
    mode: 'onSubmit',
  })

  const [incrementHours, setIncrementHours] = useState(HOURS_OPTIONS[0].value)
  const [incrementMinutes, setIncrementMinutes] = useState(MINUTES_OPTIONS[0].value)
  const [incrementSeconds, setIncrementSeconds] = useState(SECONDS_OPTIONS[0].value)
  const [isPublish, setIsPublish] = useState<{ [key: string]: boolean }>({
    'order-of-go': false,
    scoring: false,
    'prize-money': false,
  })
  const [prizeMoney, setPrizeMoney] = useState(0)
  const [editLoading, setEditLoading] = useState(false)
  const [entryFeesCollected, setEntryFeesCollected] = useState(0)
  const [isQuickToggling, setIsQuickToggling] = useState(false)
  const [isPreparingNotificationsModal, setIsPreparingNotificationsModal] = useState(false)

  const calculateEntryFeesTotal = (registrationsData: IRegistrationByDayInterface[] | null) => {
    if (!registrationsData || !Array.isArray(registrationsData)) {
      return 0
    }

    try {
      let paid = 0
      let qualifyFeePaid = 0

      registrationsData.forEach((registration) => {
        if (!registration) return

        if (
          registration.paymentStatus === PAYMENT_STATUS.PAID &&
          registration.paymentType !== PAYMENT_TYPE.DEFAULT
        ) {
          // convert to num - handle possible NaN from conversion
          const regPrice = Number(registration.registrationPrice)
          if (!isNaN(regPrice)) {
            paid += regPrice
          }

          if (registration.isQualify) {
            const qualifyFee = Number(registration?.qualifyFee || 0)
            if (!isNaN(qualifyFee)) {
              qualifyFeePaid += qualifyFee
            }
          }
        }
      })

      // format 2 decimal places
      return parseFloat((paid + qualifyFeePaid).toFixed(2))
    } catch (error) {
      console.error('Error calculating entry fees:', error)
      return 0
    }
  }

  useEffect(() => {
    try {
      if (selectedClass?.value && registrationsByDay) {
        // filter registrations for selected class
        const classRegistrations = registrationsByDay.filter(
          (registration) => registration && registration.uuid === selectedClass.value
        )

        const totalPaid = calculateEntryFeesTotal(classRegistrations)

        setEntryFeesCollected(totalPaid)
      } else {
        // set to 0 if no class selected or no registrations data
        setEntryFeesCollected(0)
      }
    } catch (error) {
      console.error('Error in entry fees calculation effect:', error)
      setEntryFeesCollected(0)
    }
  }, [selectedClass, registrationsByDay])

  // This function will handle updating the local state of registrationsGroupedByDay as well as updating firestore for each rider to support the rideTime value used for scheduling riders by the day. This function is called by the OperationsHeader when the increment changes.
  const recalculateRideTimes = async (selectedDayValue: string, increment: string) => {
    try {
      let registrationsForDay = registrationsGroupedByDay[selectedDayValue] || []

      registrationsForDay = sortByOrder(registrationsForDay)

      //  Find the earliest existing rideTime (excluding breaks)
      let firstRider = registrationsForDay.find(
        (reg) => 'rideTime' in reg && typeof reg.rideTime === 'string'
      )

      let startTime = firstRider?.rideTime || '08:00:00' // Default if no valid rideTime found

      // Parse start time and increment
      let [startHours, startMinutes, startSeconds] = startTime.split(':').map(Number)
      let [incHours, incMinutes, incSeconds] = increment.split(':').map(Number)

      let currentTime = new Date(1970, 0, 1, startHours, startMinutes, startSeconds)

      // Assign new ride times
      const updatedRegistrations = registrationsForDay.map((registration) => {
        if ('isBreakDrag' in registration && registration.isBreakDrag) {
          //  Handle breaks: Adjust the schedule but don't assign rideTime
          let [breakHours, breakMinutes, breakSeconds] =
            'time' in registration && typeof registration.time === 'string'
              ? registration.time.split(':').map(Number)
              : [0, 0, 0] // Default values to prevent errors

          currentTime.setHours(currentTime.getHours() + breakHours)
          currentTime.setMinutes(currentTime.getMinutes() + breakMinutes)
          currentTime.setSeconds(currentTime.getSeconds() + breakSeconds)

          return { ...registration } // No rideTime for breaks
        }

        let rideTime = new Date(currentTime) // Copy current time
        let formattedRideTime = rideTime.toTimeString().substring(0, 8)

        // Update time for next rider
        currentTime.setMinutes(currentTime.getMinutes() + incHours * 60 + incMinutes)
        currentTime.setSeconds(currentTime.getSeconds() + incSeconds)

        return { ...registration, rideTime: formattedRideTime }
      })

      // Filter out the break items from Firestore updates
      const registrationsToUpdate = updatedRegistrations.filter(
        (reg) => !('isBreakDrag' in reg && reg.isBreakDrag)
      )

      // Update Firestore only for actual registrations (not breaks)
      const updatePromises = registrationsToUpdate.map(async (registration) => {
        try {
          const updatedData = new RegistrationByDayModel({
            ...(registration as IRegistrationByDayInterface),
          })
          await FirestoreService.updateItem(
            CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.REGISTRATION_BY_DAY.NAME,
            registration.id,
            updatedData.toFirestore()
          )
        } catch (error) {
          console.error(`error`, error)
        }
      })

      await Promise.all(updatePromises)

      // Update local state
      setRegistrationsGroupedByDay({
        ...registrationsGroupedByDay,
        [selectedDayValue]: updatedRegistrations,
      })
    } catch (error) {
      console.error('error', error)
    }
  }

  const updateGroupPublishStatus = async (
    type: 'riderGroupPublish' | 'classGroupPublish',
    groupKey: string,
    tab: IOperationsTabs,
    value: boolean
  ) => {
    const key =
      tab === 'order-of-go' ? 'publishOrder' : tab === 'scoring' ? 'publishScore' : 'publishPrize'

    setIsPublish((prev) => ({
      ...prev,
      [tab]: value,
    }))

    try {
      const EventSchedule = publishedEvent?.EventSchedule
      const currentRiderGroupPublish = EventSchedule?.[type] || {}

      //  preserve existing data - updating only the selected groupKey
      currentRiderGroupPublish[groupKey] = {
        ...currentRiderGroupPublish[groupKey], // keep existing data for the group
        [key]: value,
      }

      const updated = {
        EventSchedule: {
          ...EventSchedule,
          [type]: currentRiderGroupPublish,
        },
      }

      await FirestoreService.updateItem(COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME, eventId, updated)
      await FirestoreService.updateItem(COLLECTIONS.EVENT_SCHEDULING.NAME, eventId, {
        [type]: currentRiderGroupPublish,
      })

      if (publishedEvent) {
        setPublishedEvent({
          ...publishedEvent,
          ...updated,
        })
      }
    } catch (error) {
      console.error('Error updating rider group publish status:', error)
    }
  }

  const onChangeMoneyPrize = async (event: ChangeEvent<HTMLInputElement>) => {
    try {
      await updateFeesKeys({ prizeMoney: event.target.value }, [
        { uuid: selectedClass?.value || '', parentId: selectedClass?.parentId },
      ])
    } catch (error) {
      console.error(error)
      toastFunctions.error({
        message: MESSAGES_CONST.EVENT_INCREMENT_NOT_UPDATED,
      })
    }
  }

  const onchangeIncrementHandler = async (value: string, type: DateEnum) => {
    let time

    try {
      switch (type) {
        case DateEnum.h:
          setIncrementHours(value)
          time = `${value}:${incrementMinutes}:${incrementSeconds}`
          break
        case DateEnum.m:
          setIncrementMinutes(value)
          time = `${incrementHours}:${value}:${incrementSeconds}`
          break
        default:
          setIncrementSeconds(value)
          time = `${incrementHours}:${incrementMinutes}:${value}`
      }

      setIncrement(time)

      if (scheduleBy === 'class') {
        await updateFeesKeys({ increment: time }, [
          { uuid: selectedClass?.value || '', parentId: selectedClass?.parentId },
        ])
        toastFunctions.success({ message: MESSAGES_CONST.EVENT_INCREMENT_UPDATED })
      } else if (scheduleBy === 'rider' && selectedDay) {
        handleModalContext?.handleModal(true, MODAL_CONSTS.RESCHEDULE_RIDE_TIMES, {
          dayKey: selectedDay.value,
          currentRegistrations: registrationsGroupedByDay[selectedDay.value],
          increment: time,
          recalculateRideTimes,
        })
      }
    } catch (error) {
      console.error(error)
      toastFunctions.error({
        message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
      })
    }
  }

  const prepareClassPublishNotificationsData = async () => {
    const publishItems: PublishItem[] = []

    try {
      const eventReviewPublishSnap = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId
      )
      const eventReviewPublish =
        EventReviewPublishModel.fromFirestoreDoc(eventReviewPublishSnap).toObject()
      const EventSchedule = eventReviewPublish.EventSchedule

      const groupPublish = EventSchedule?.classGroupPublish || {}

      // calc total riders across all classes
      const totalRiders = classOptions.reduce((sum, classOption) => {
        return sum + (classOption.entryCount || 0)
      }, 0)

      // get unique facilities from classOptions
      const uniqueFacilities = new Set<string>()
      classOptions.forEach((classOption) => {
        if (classOption.facility) {
          uniqueFacilities.add(classOption.facility)
        }
      })
      const totalFacilities = uniqueFacilities.size

      // get all-classes last sent times
      const groupPublishAll = groupPublish['all-classes']

      publishItems.push({
        id: 'all-classes',
        title: 'All classes',
        subtitle: `${classOptions.length} classes • ${totalFacilities} facilities • ${totalRiders} rides`,
        orderOfGoStatus: groupPublishAll?.publishOrder ?? false,
        scoresStatus: groupPublishAll?.publishScore ?? false,
        orderOfGoLastSent: formatNotificationTimestamp(groupPublishAll?.orderOfGoLastSent ?? null),
        scoresLastSent: formatNotificationTimestamp(groupPublishAll?.scoresLastSent ?? null),
      })

      // add individual classes with detailed logging
      classOptions.forEach((classOption) => {
        const groupPublishByValue = groupPublish[classOption.value] || {}

        publishItems.push({
          id: classOption.value,
          title: classOption.className || '',
          subtitle: `${classOption.facility} • ${classOption.entryCount} rides`,
          orderOfGoStatus: groupPublishByValue?.publishOrder ?? false,
          scoresStatus: groupPublishByValue?.publishScore ?? false,
          orderOfGoLastSent: formatNotificationTimestamp(
            groupPublishByValue?.orderOfGoLastSent ?? null
          ),
          scoresLastSent: formatNotificationTimestamp(groupPublishByValue?.scoresLastSent ?? null),
        })
      })

      return { publishItems }
    } catch (error) {
      console.error('Error preparing class publish notifications data:', error)
      return { publishItems: [] }
    }
  }

  const prepareRiderPublishNotificationsData = async () => {
    const publishItems: PublishItem[] = []

    try {
      const eventReviewPublishSnap = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId
      )
      const eventReviewPublish =
        EventReviewPublishModel.fromFirestoreDoc(eventReviewPublishSnap).toObject()
      const EventSchedule = eventReviewPublish.EventSchedule
      const groupPublish = EventSchedule?.riderGroupPublish || {}

      // all registrations from all days
      const allRegistrations = dayOptions
        .flatMap((dayOption) => registrationsGroupedByDay[dayOption.value] || [])
        .filter((reg) => !('isBreakDrag' in reg && reg.isBreakDrag))

      // get all-classes data if exists
      const groupPublishAll = groupPublish['all-classes']

      // subtitle data
      const totalDays = dayOptions.length

      //  unique arenas (fee.location)
      const uniqueArenas = new Set<string>()
      allRegistrations.forEach((reg) => {
        if (reg.fee?.location) {
          uniqueArenas.add(reg.fee.location)
        }
      })
      const totalArenas = uniqueArenas.size

      // total rides across all days
      const totalRides = allRegistrations.length

      publishItems.push({
        id: 'all-classes',
        title: 'All classes',
        subtitle: `${totalDays} days • ${totalArenas} arenas • ${totalRides} rides`,
        orderOfGoStatus: groupPublishAll?.publishOrder ?? false,
        scoresStatus: groupPublishAll?.publishScore ?? false,
        orderOfGoLastSent: formatNotificationTimestamp(groupPublishAll?.orderOfGoLastSent ?? null),
        scoresLastSent: formatNotificationTimestamp(groupPublishAll?.scoresLastSent ?? null),
      })

      dayOptions.forEach((dayOption) => {
        const title = `${dayOption.formattedDay} rides`
        const subtitle = ` ${dayOption.facility} • ${dayOption.entryCount} rides`

        // get registrations for each day to determine scores publish status
        const registrationsForDay = registrationsGroupedByDay[dayOption.value] || []
        const actualRegistrations = registrationsForDay.filter(
          (reg) => !('isBreakDrag' in reg && reg.isBreakDrag)
        )

        // get unique classes for this day
        const uniqueClasses = new Set<string>()
        actualRegistrations.forEach((registration) => {
          if (registration.fee?.uuid) {
            uniqueClasses.add(registration.fee.uuid)
          }
        })

        // get the group data for a specific group
        const groupPublishByValue = groupPublish[dayOption.value]

        publishItems.push({
          id: dayOption.value,
          title,
          subtitle,
          orderOfGoStatus: groupPublishByValue?.publishOrder ?? false,
          scoresStatus: groupPublishByValue?.publishScore ?? false,
          orderOfGoLastSent: formatNotificationTimestamp(
            groupPublishByValue?.orderOfGoLastSent ?? null
          ),
          scoresLastSent: formatNotificationTimestamp(groupPublishByValue?.scoresLastSent ?? null),
        })
      })

      return { publishItems }
    } catch (error) {
      console.error('Error preparing publish notifications data:', error)
      return { publishItems: [] }
    }
  }

  const handleQuickPublishToggle = async (
    tab: IOperationsTabs,
    type: 'riderGroupPublish' | 'classGroupPublish'
  ) => {
    let groupKey

    if (activeTab === 'prize-money') {
      if (isQuickToggling || !selectedClass?.value) return
      groupKey = selectedClass.value
    } else if (type === 'riderGroupPublish') {
      if (isQuickToggling || !selectedDay?.value) return
      groupKey = selectedDay.value
    } else if (type === 'classGroupPublish') {
      if (isQuickToggling || !selectedClass?.value) return
      groupKey = selectedClass.value
    }

    if (!groupKey) return

    try {
      setIsQuickToggling(true)
      await updateGroupPublishStatus(type, groupKey, tab, !isPublish[tab])

      // we need to update the score status to 'Did Not Place' when class is live
      if (activeTab === 'scoring' && !isPublish[tab] && scheduleBy === 'class') {
        const registrationsByDayUpdated = await updateScoreStatus(registrationsByDay, fee)
        setRegistrationsByDay([...registrationsByDayUpdated])
      }

      toastFunctions.success({ message: MESSAGES_CONST.VISIBILITY_UPDATED })
    } catch (error) {
      setIsPublish((prev) => ({
        ...prev,
        [tab]: isPublish[tab],
      }))

      console.error('Error toggling publish status:', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setIsQuickToggling(false)
    }
  }

  const onSavePublishNotification = async (scheduleByType?: TGroupPublish) => {
    try {
      const publishedEventInDbSnapShot = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId
      )

      const publishedEventInDb = getConvertedData(
        EventReviewPublishModel.fromFirestoreDoc(publishedEventInDbSnapShot).toObject()
      )

      if (publishedEvent) setPublishedEvent(publishedEventInDb)

      // we need to update the score status to 'Did Not Place' when class is live
      if (activeTab === 'scoring' && scheduleBy === 'class') {
        const isPublishScore = scheduleByType?.[fee?.uuid || '']?.publishScore
        if (isPublishScore) {
          const registrationsByDayUpdated = await updateScoreStatus(registrationsByDay, fee)
          setRegistrationsByDay([...registrationsByDayUpdated])
        }
      }

      toastFunctions.success({ message: MESSAGES_CONST.PUBLISHING_STATUSES_UPDATED })
    } catch (error) {
      console.error('error', error)
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    }
  }

  useEffect(() => {
    if (selectedClass?.value && registrationFees && scheduleBy === 'class') {
      let registrationFees_: any
      registrationFees.registrationFees?.forEach((item) => {
        if (item.uuid === selectedClass.value) {
          registrationFees_ = { ...item }
        }

        if (item?.children && Array.isArray(item.children)) {
          item.children.forEach((childItem) => {
            if (childItem.uuid === selectedClass.value) {
              registrationFees_ = { ...childItem, increment: childItem.increment || '' }
            }
          })
        }
      })

      if (!registrationFees_) {
        console.error('registrationFees_', registrationFees_)
        return
      }

      const incrementTime =
        registrationFees_.increment?.length === 8 ? registrationFees_.increment : '00:00:00'

      const [incrementBaseHours, incrementBaseMinutes, incrementBaseSeconds] =
        incrementTime.split(/[:\s]/)
      setIncrementHours(
        incrementBaseHours?.length === 2 && incrementBaseHours > 0 ? incrementBaseHours : '00'
      )
      setIncrementMinutes(
        incrementBaseMinutes?.length === 2 && incrementBaseMinutes > 0 ? incrementBaseMinutes : '00'
      )
      setIncrementSeconds(
        incrementBaseSeconds?.length === 2 && incrementBaseSeconds > 0 ? incrementBaseSeconds : '00'
      )

      setIncrement(incrementTime)
    }
  }, [registrationFees, selectedClass, activeTab, scheduleBy, setIncrement])

  useEffect(() => {
    if (registrationFees) {
      const registrationFee = getRegistrationFee(
        registrationFees?.registrationFees ?? [],
        selectedClass?.value ?? '',
        selectedClass?.parentId
      )
      setPrizeMoney(registrationFee?.prizeMoney ?? 0)
    }
  }, [registrationFees, selectedClass])

  useEffect(() => {
    // If the tab is prize money OR the scheduling mode is class, treat it as class mode for dropdown logic.
    const isClassMode =
      activeTab === MANAGE_OPERATIONS_CONSTS.TAB_NAMES.PRIZE_MONEY.value || scheduleBy === 'class'

    if (isClassMode && classOptions.length > 0 && !selectedClass?.value) {
      // If we are in class mode and we have class options but no selected class, pick the first class.
      setSelectedClass(classOptions[0])
    } else if (scheduleBy === 'rider' && dayOptions.length > 0 && !selectedDay) {
      // if the user is in rider scheduling mode and we have day options but no selected day, pick the first day.
      setSelectedDay(dayOptions[0])
    }
  }, [
    activeTab,
    scheduleBy,
    dayOptions,
    classOptions,
    selectedDay,
    setSelectedDay,
    selectedClass,
    setSelectedClass,
  ])

  useEffect(() => {
    let groupPublish
    const EventSchedule = publishedEvent?.EventSchedule

    if (activeTab === 'prize-money' && selectedClass?.value) {
      if (scheduleBy === 'rider') {
        groupPublish = EventSchedule?.riderGroupPublish?.[selectedClass.value]
      } else {
        groupPublish = EventSchedule?.classGroupPublish?.[selectedClass.value]
      }
    } else if (scheduleBy === 'rider' && selectedDay?.value) {
      groupPublish = EventSchedule?.riderGroupPublish?.[selectedDay.value]
    } else if (scheduleBy === 'class' && selectedClass?.value) {
      groupPublish = EventSchedule?.classGroupPublish?.[selectedClass.value]
    }

    setIsPublish({
      'order-of-go': groupPublish?.publishOrder ?? false,
      scoring: groupPublish?.publishScore ?? false,
      'prize-money': groupPublish?.publishPrize ?? false,
    })
  }, [scheduleBy, selectedDay, selectedClass, activeTab, publishedEvent])

  return (
    <div className="relative z-30 flex flex-wrap gap-2 items-center justify-between">
      <div className="flex flex-wrap min-h-[42px] items-center">
        {MANAGE_OPERATIONS_CONSTS.TABS.map((currentItem, index) => {
          return (
            <div
              key={index}
              onClick={() => setActiveTab(currentItem.id as IOperationsTabs)}
              className={`py-2 px-4 text-[14px] cursor-pointer mr-2 rounded-xl ${
                activeTab === currentItem.id
                  ? 'bg-SeabiscuitMainThemeColor/5 text-SeabiscuitMainThemeColor'
                  : 'text-SeabiscuitDark200ThemeColor'
              }`}
            >
              {currentItem.tab}
            </div>
          )
        })}
      </div>
      <div className="relative z-20 flex flex-[1_0_auto] flex-wrap gap-2 ml-auto justify-end max-w-full">
        {scheduleBy === 'rider' &&
          (activeTab === 'order-of-go' || activeTab === 'scoring') &&
          selectedDay && (
            <div className="w-full sm:w-[200px] flex justify-end">
              <div className="relative w-full sm:w-auto inline-flex rounded-lg border border-SeabiscuitGray500ThemeColor hover:border-SeabiscuitGray600ThemeColor transition-all">
                <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <IconSearch />
                </div>
                <input
                  type="search"
                  id="default-search"
                  className="block w-full sm:w-[41px] sm:hover:w-[200px] sm:[&:not(:placeholder-shown)]:w-[200px] bg-transparent p-0 h-[42px] pl-10 text-nr border-0 text-SeabiscuitDark200ThemeColor focus:ring-0 focus:outline-none rounded-lg placeholder:text-nr transition-all"
                  placeholder="Search rider"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
              </div>
            </div>
          )}
        {activeTab !== 'judging' && (
          <Controller
            name="title"
            control={control}
            render={() => {
              const dropDownOptions = isClassDropdown ? classOptions : dayOptions

              const dropdownPlaceholder = isClassDropdown ? 'Select Class' : 'Select Day'
              return (
                <Select
                  isClearable={false}
                  isSearchable={true}
                  placeholder={dropdownPlaceholder}
                  menuShouldScrollIntoView={true}
                  formatOptionLabel={formatOptionLabel}
                  value={
                    isClassDropdown
                      ? dropDownOptions.find((option) => option.value === selectedClass?.value)
                      : dropDownOptions.find((option) => option.value === selectedDay?.value)
                  }
                  options={dropDownOptions}
                  onChange={(newValue) => {
                    if (isClassDropdown) {
                      setSelectedClass({
                        value: newValue?.value ?? '',
                        label: newValue?.label || '',
                        parentId: newValue?.parentId,
                      })
                    } else {
                      setSelectedDay({
                        value: newValue?.value ?? '',
                        label: newValue?.label || '',
                      })
                    }
                  }}
                  className="flex-1 min-w-[200px] max-w-[400px]"
                  styles={customSelectStyles}
                />
              )
            }}
          />
        )}
        {activeTab === 'order-of-go' && (selectedClass?.value || selectedDay) && (
          <>
            {scheduleBy === 'rider' && selectedDay && isTableInEditMode && (
              <GapTimeSelector onTimeChange={setGapTime} clashCount={clashCount || 0} />
            )}
            {scheduleBy === 'class' && selectedClass?.value && (
              <Controller
                name="title"
                control={control}
                render={() => (
                  <div className="flex gap-2 start-time-wrapper items-center border border-solid border-SeabiscuitGray500ThemeColor hover:border-SeabiscuitGray600ThemeColor rounded-lg p-2">
                    <p className="text-nr">Class Start:</p>
                    <span className="text-nr">{`${fee?.startTimeHours}:${fee?.startTimeMinutes} ${fee?.startTimeFormat}`}</span>
                    <button
                      type="button"
                      className="w-4 transition-all hover:opacity-70"
                      onClick={async () => {
                        setEditLoading(true)
                        await FirestoreService.updateItem(
                          CONST.DATA.FIRESTORE.V01.COLLECTIONS.EVENTS.NAME,
                          eventId,
                          {
                            eventFormState: CONST.UI.EVENTS.CREATE_EDIT.TABS.SCHEDULING.VALUE,
                          }
                        )
                        history.push(`${CONST.ROUTES.ORGANIZER_EDIT.URL}/${eventId}`)
                        setEditLoading(false)
                      }}
                    >
                      {editLoading ? (
                        <img
                          className="w-full animate-spin"
                          src="/assets/og_icons/Synchronize.svg"
                          alt=""
                        />
                      ) : (
                        <img className="w-full" src="/assets/cp_icons/edit.svg" alt="" />
                      )}
                    </button>
                  </div>
                )}
              />
            )}

            {scheduleBy === 'class' && selectedClass?.value ? (
              <Controller
                name="title"
                control={control}
                render={() => (
                  <div className="flex start-time-wrapper items-center border border-solid border-SeabiscuitGray500ThemeColor hover:border-SeabiscuitGray600ThemeColor rounded-lg px-2">
                    <p className={'mr-2 text-nr'}>Ride time:</p>
                    <GlobalInputComponent
                      type="select"
                      className="flex-1 px-[0!important] h-[40px] flex items-center border-none outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-white"
                      placeholder="00"
                      defaultValue={incrementHours}
                      onChange={(value) => onchangeIncrementHandler(value, DateEnum.h)}
                      value={HOURS_OPTIONS}
                      control={control}
                      isTimeSelector={true}
                      isdisableInput={true}
                    />
                    :
                    <GlobalInputComponent
                      type="select"
                      className="flex-1 px-[0!important] h-[40px] border-none flex items-center outline-0 text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-white !rounded-none"
                      placeholder="00"
                      defaultValue={incrementMinutes}
                      value={MINUTES_OPTIONS}
                      onChange={(value) => onchangeIncrementHandler(value, DateEnum.m)}
                      isTimeSelector={true}
                      isdisableInput={true}
                      control={control}
                    />
                    :
                    <GlobalInputComponent
                      type="select"
                      className="flex-1 px-[0!important] h-[40px] outline-0 flex items-center text-gray-500 w-full focus:ring-0 focus:border-0 focus:outline-0 bg-white !rounded-r-[4px] !rounded-l-non"
                      placeholder="00"
                      defaultValue={incrementSeconds}
                      value={SECONDS_OPTIONS}
                      onChange={(value) => onchangeIncrementHandler(value, DateEnum.s)}
                      isTimeSelector={true}
                      isdisableInput={true}
                      control={control}
                    />
                  </div>
                )}
              />
            ) : null}
          </>
        )}
        {activeTab === 'prize-money' && (
          <div className="flex gap-2 items-center rounded-md border border-solid border-SeabiscuitGray500ThemeColor px-2.5 py-0.5">
            <Tooltip
              title={
                <h1 className="tooltip_title max-w-[100px] text-center py-3">
                  Enter the total amount you wish to award in this class.
                  <br />
                  <br />
                  Class entry fees collected:
                  <br />
                  <span className="text-[18px] font-bold">${entryFeesCollected}</span>
                </h1>
              }
              placement="top"
              arrow
            >
              <button>
                <IoMdInformationCircleOutline />
              </button>
            </Tooltip>
            Prize money:
            <input
              value={prizeMoney > 0 ? prizeMoney : ''}
              type="number"
              className="p-0 ml-2 w-[100px] outline-0 border-0 text-SeabiscuitDark200ThemeColor text-base text-right hover:opacity-70 transition-all"
              placeholder={`${currency}0`}
              onChange={(event) => setPrizeMoney(Number(event.target.value))}
              onBlur={onChangeMoneyPrize}
            />
          </div>
        )}

        <div className="flex items-center gap-2">
          {(selectedClass?.value || selectedDay) && activeTab !== 'judging' && (
            <>
              {scheduleBy === 'class' && (
                <>
                  <PrivateLiveToggle
                    isOn={isPublish[activeTab]}
                    disabled={isQuickToggling}
                    loading={isQuickToggling}
                    onToggle={() => handleQuickPublishToggle(activeTab, 'classGroupPublish')}
                    variant="rectangular"
                  />
                  {activeTab !== 'prize-money' && (
                    <button
                      onClick={async () => {
                        if (isPreparingNotificationsModal) return

                        setIsPreparingNotificationsModal(true)

                        try {
                          // prepare data before opening modal
                          const { publishItems } = await prepareClassPublishNotificationsData()

                          handleModalContext?.handleModal(
                            true,
                            MODAL_CONSTS.PUBLISH_NOTIFICATIONS_MODAL,
                            {
                              publishItems,
                              eventId,
                              registrationsByDay,
                              scheduleBy: 'class',
                              onSavePublish: async (scheduleByType: TGroupPublish) =>
                                await onSavePublishNotification(scheduleByType),
                            }
                          )
                        } catch (error) {
                          console.error('Error preparing modal data:', error)
                          toastFunctions.error({
                            message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
                          })
                        } finally {
                          setIsPreparingNotificationsModal(false)
                        }
                      }}
                      disabled={isPreparingNotificationsModal}
                      className={`bg-SeabiscuitGrayThemeColor hover:bg-SeabiscuitMainThemeColor rounded-lg h-[42px] w-[42px] p-2 flex-shrink-0 flex items-center justify-center transition-all duration-200 group ${
                        isPreparingNotificationsModal ? 'opacity-50' : ''
                      }`}
                    >
                      {isPreparingNotificationsModal ? (
                        <ImSpinner8 className="h-[18px] w-[18px] text-white animate-spin" />
                      ) : (
                        <img
                          src={'assets/cp_icons/bell-theme-icon-full.svg'}
                          alt="bell icon"
                          className="h-[18px] w-[18px] group-hover:scale-110 group-hover:rotate-12 group-hover:brightness-0 group-hover:invert transition-transform duration-200"
                        />
                      )}
                    </button>
                  )}
                </>
              )}

              {scheduleBy === 'rider' &&
                ((activeTab === 'prize-money' && selectedClass?.value) ||
                  (activeTab !== 'prize-money' && selectedDay)) && (
                  <PrivateLiveToggle
                    isOn={isPublish[activeTab]}
                    disabled={isQuickToggling}
                    onToggle={() => handleQuickPublishToggle(activeTab, 'riderGroupPublish')}
                    variant="rectangular"
                  />
                )}
            </>
          )}

          {scheduleBy === 'rider' && (activeTab === 'order-of-go' || activeTab === 'scoring') && (
            <button
              onClick={async () => {
                if (isPreparingNotificationsModal) return

                setIsPreparingNotificationsModal(true)

                try {
                  // prepare data before opening modal
                  const { publishItems } = await prepareRiderPublishNotificationsData()

                  handleModalContext?.handleModal(true, MODAL_CONSTS.PUBLISH_NOTIFICATIONS_MODAL, {
                    publishItems,
                    eventId,
                    registrationsGroupedByDay,
                    scheduleBy: 'rider',
                    onSavePublish: async () => await onSavePublishNotification(),
                  })
                } catch (error) {
                  console.error('Error preparing modal data:', error)
                  toastFunctions.error({
                    message: MESSAGES_CONST.SOMETHING_WENT_WRONG,
                  })
                } finally {
                  setIsPreparingNotificationsModal(false)
                }
              }}
              disabled={isPreparingNotificationsModal}
              className={`bg-SeabiscuitGrayThemeColor hover:bg-SeabiscuitMainThemeColor rounded-lg h-[42px] w-[42px] p-2 flex-shrink-0 flex items-center justify-center transition-all duration-200 group ${
                isPreparingNotificationsModal ? 'opacity-50' : ''
              }`}
            >
              {isPreparingNotificationsModal ? (
                <ImSpinner8 className="h-[18px] w-[18px] text-white animate-spin" />
              ) : (
                <img
                  src={'assets/cp_icons/bell-theme-icon-full.svg'}
                  alt="bell icon"
                  className="h-[18px] w-[18px] group-hover:scale-110 group-hover:rotate-12 group-hover:brightness-0 group-hover:invert transition-transform duration-200"
                />
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
